import {
  faCommentDots,
  faComputer,
  faUser,
  faPerson,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format } from 'date-fns'
import type React from 'react'
import { Fragment } from 'react'
import styled from 'styled-components'

import type {
  Touchpoint,
  TouchpointAuthor,
  TouchpointAuthorType,
} from '@/types/Touchpoints'
import { Colours } from '@/utils/styles'

interface Props {
  index: number
  personId: string
  touchpoint: Touchpoint
  deleteTouchpoint: (id: string) => void
}

const MessageContentWrapper = styled.div<{ deleted: boolean }>`
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  #message {
    color: ${props => (props.deleted ? Colours.veryLightGray : '#082842')};
  }
`

const DeleteMessage = styled.button`
  background: none;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 5px;
  padding: 5px 10px;

  &:hover {
    cursor: pointer;
    color: #fff;
    background: ${Colours.red};
  }
`

const TimeContainer = styled.div`
  text-align: end;
`

const AuthorDetails = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 90px;
  grid-gap: 10px;
`

const Message = styled.div<{ type: TouchpointAuthorType }>`
  padding-bottom: 80px;
  padding-left: 70px;
  padding-right: 30px;
  position: relative;
  background-color: inherit;
  width: 100%;
  white-space: pre-wrap;
`

const authorColourMap: Record<TouchpointAuthorType, string> = {
  agent: Colours.red,
  system: Colours.black,
  applicant: Colours.orange,
  person: Colours.teal,
}

const IconWrapper = styled.div<{ type: TouchpointAuthorType }>`
  position: absolute;
  width: 35px;
  height: 35px;
  left: 8px;
  padding-top: 2px;
  text-align: center;
  z-index: 1;
  border-radius: 50%;
  color: white;
  background-color: ${props => authorColourMap[props.type]};
  border: 7px solid ${props => authorColourMap[props.type]};
`

const AuthorLabel = styled.div<{ deleted: boolean }>`
  color: ${props => (props.deleted ? Colours.red : '#082842')};
`

const toAuthorLabel = (author: TouchpointAuthor): string =>
  author.type === 'system'
    ? 'System'
    : author.type === 'agent'
      ? author?.details?.email
        ? `(Agent) ${author?.details?.email}`
        : 'Agent'
      : author.type === 'applicant'
        ? 'By Applicant'
        : `By ${author?.details?.email}`

const TouchpointMessage: React.FC<Props> = ({
  deleteTouchpoint,
  index,
  personId,
  touchpoint: { message, author, createdAt, softDeleted, id },
}) => {
  return (
    <Fragment key={index}>
      <Message type={author.type}>
        <IconWrapper type={author.type}>
          <FontAwesomeIcon
            icon={
              author.type === 'agent'
                ? faCommentDots
                : author.type === 'system'
                  ? faComputer
                  : author.type === 'applicant'
                    ? faPerson
                    : faUser
            }
          />
        </IconWrapper>
        <AuthorDetails className="side-by-side-flex">
          <AuthorLabel deleted={softDeleted}>
            {toAuthorLabel(author)} {softDeleted && <span>(Deleted)</span>}
          </AuthorLabel>
          <div>
            <TimeContainer>{format(new Date(createdAt), 'dd/MM/yyyy')}</TimeContainer>
            <TimeContainer>{format(new Date(createdAt), 'HH:mm')}</TimeContainer>
          </div>
        </AuthorDetails>
        <MessageContentWrapper deleted={softDeleted}>
          <p id="message">{message}</p>
          {personId === author.id && !softDeleted && (
            <DeleteMessage onClick={() => deleteTouchpoint(id)}>Delete</DeleteMessage>
          )}
        </MessageContentWrapper>
        {softDeleted && (
          <strong>
            <i>
              <p>
                This comment has been deleted but will remain visible here. The agent will
                not be able to see it.
              </p>
            </i>
          </strong>
        )}
      </Message>
    </Fragment>
  )
}

export default TouchpointMessage
